import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Search, Plus, Box, Loader2, Sparkles } from "lucide-react";
import type { AssignedStructure, BodyRegionStructure } from "@/hooks/use-body-region-structures";

export interface StructurePanelProps {
  sectionToken: string;
  /** Structures already assigned to this section for this template */
  assignedStructures: AssignedStructure[];
  /** All structures available in the body region pool for this section */
  availableStructures: BodyRegionStructure[];
  /** Cross-modality search results */
  searchResults: BodyRegionStructure[];
  /** Loading state for available structures */
  loading: boolean;
  /** Loading state for search */
  searchLoading: boolean;
  /** Whether updates are in progress */
  updating: boolean;
  /** Handler to toggle a structure on/off for this section */
  onToggleStructure: (structureKey: string, include: boolean) => Promise<boolean>;
  /** Handler for cross-modality search */
  onSearch: (query: string) => void;
  /** Handler to clear search */
  onClearSearch: () => void;
  /** Handler to open custom structure creation dialog */
  onOpenCreateStructure?: () => void;
}

export function StructurePanel({
  sectionToken,
  assignedStructures,
  availableStructures,
  searchResults,
  loading,
  searchLoading,
  updating,
  onToggleStructure,
  onSearch,
  onClearSearch,
  onOpenCreateStructure,
}: StructurePanelProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Structures assigned to THIS section
  const sectionStructures = useMemo(
    () => assignedStructures.filter((s) => s.section_token === sectionToken),
    [assignedStructures, sectionToken]
  );

  // Available structures for this section that aren't already assigned
  const assignedKeys = useMemo(
    () => new Set(sectionStructures.map((s) => s.structure_key)),
    [sectionStructures]
  );

  // Show all unassigned structures from the body region pool
  // The section_token in body_region_structure_pool is a default hint, not a strict filter
  // This allows any structure from the relevant body region to be added to any section
  const unassignedAvailable = useMemo(
    () =>
      availableStructures
        .filter((s) => !assignedKeys.has(s.structure_key))
        .slice(0, 20), // Limit display
    [availableStructures, assignedKeys]
  );

  // Filter search results to exclude already assigned
  const filteredSearchResults = useMemo(
    () => searchResults.filter((s) => !assignedKeys.has(s.structure_key)).slice(0, 30),
    [searchResults, assignedKeys]
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.length >= 2) {
      onSearch(value);
    } else {
      onClearSearch();
    }
  };

  const handleAddFromSearch = async (structure: BodyRegionStructure) => {
    await onToggleStructure(structure.structure_key, true);
    setSearchQuery("");
    onClearSearch();
    setPopoverOpen(false);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Box className="h-4 w-4" />
          <span>Structures</span>
          {sectionStructures.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {sectionStructures.length}
            </Badge>
          )}
        </div>

        {/* Cross-modality search popover */}
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
              <Plus className="h-3 w-3 mr-1" />
              Add Structure
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3" align="end">
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search all structures..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-8 h-8 text-sm"
                  autoFocus
                />
                {searchLoading && (
                  <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>

              <ScrollArea className="h-48">
                {searchQuery.length >= 2 ? (
                  filteredSearchResults.length > 0 ? (
                    <div className="space-y-1">
                      {filteredSearchResults.map((structure) => (
                        <button
                          key={structure.structure_key}
                          className="w-full text-left px-2 py-1.5 rounded hover:bg-muted transition-colors"
                          onClick={() => handleAddFromSearch(structure)}
                          disabled={updating}
                        >
                          <div className="text-sm font-medium">
                            {structure.display_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {structure.body_region}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No structures found
                    </p>
                  )
                ) : unassignedAvailable.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground mb-2">
                      Suggested for this section:
                    </p>
                    {unassignedAvailable.map((structure) => (
                      <button
                        key={structure.structure_key}
                        className="w-full text-left px-2 py-1.5 rounded hover:bg-muted transition-colors"
                        onClick={() => handleAddFromSearch(structure)}
                        disabled={updating}
                      >
                        <div className="text-sm">{structure.display_name}</div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Type to search structures
                  </p>
                )}
              </ScrollArea>

              {/* Create Custom Structure button */}
              {onOpenCreateStructure && (
                <div className="pt-2 border-t mt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-xs"
                    onClick={() => {
                      setPopoverOpen(false);
                      onOpenCreateStructure();
                    }}
                  >
                    <Sparkles className="h-3 w-3 mr-2" />
                    Create custom structure
                  </Button>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Assigned structures list */}
      {sectionStructures.length > 0 ? (
        <div className="space-y-1">
          {sectionStructures.map((structure) => (
            <div
              key={structure.structure_key}
              className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                checked={true}
                onCheckedChange={(checked) =>
                  onToggleStructure(structure.structure_key, !!checked)
                }
                disabled={updating}
              />
              <span className="text-sm flex-1">{structure.display_name}</span>
              {structure.enforcement_policy && (
                <Badge variant="outline" className="text-xs">
                  {structure.enforcement_policy}
                </Badge>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          No structures assigned to this section
        </p>
      )}

      {/* Quick add from available pool */}
      {unassignedAvailable.length > 0 && sectionStructures.length < 5 && (
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-2">Quick add:</p>
          <div className="flex flex-wrap gap-1">
            {unassignedAvailable.slice(0, 5).map((structure) => (
              <Badge
                key={structure.structure_key}
                variant="outline"
                className="cursor-pointer hover:bg-primary/10 hover:text-primary hover:border-primary transition-colors text-xs"
                onClick={() => onToggleStructure(structure.structure_key, true)}
              >
                <Plus className="h-3 w-3 mr-1" />
                {structure.display_name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
